import { AxiosError } from 'axios';
import {
  either,
  flatten,
  fromPairs,
  isNil,
  join,
  last,
  map,
  pathOr,
  pipe,
  prop,
  reject,
  values
} from 'ramda';
import { useHistory } from 'react-router';
import { scroller } from 'react-scroll';
import { ReduxStore } from '~/App/shared/interfaces/store';
import { translateRoute } from '~/App/shared/localization';
import { LEAD_INITIATION_TYPES } from '~/config/constants';
import { isAxiosError } from '~/helpers/axiosUtils';
import { requestErrorHandler } from '~/helpers/notifyError';
import {
  createBusinessProposal,
  createInquiry,
  getBankIdAutoStartToken,
  getLeadObject,
  getProposal
} from '~/helpers/orchestration/sale';
import {
  getDoneRouteForBusinessProposal,
  getProposalErrorMessage,
  getShouldUseFallbackSolution
} from '~/helpers/sell-flow';
import { ReduxDispatch } from '../interfaces/Redux';
import {
  AffiliateTrackingInput,
  BusinessProposalInput
} from '../interfaces/Sale';
import { getRetrackClick } from '~/helpers/client/retrackTracking';
import { initialDefaultContactDetails } from '../reducers/sellFlow1Reducer';
import {
  addonsSelected,
  odometerReading,
  packageSelected,
  registrationPlate,
  salesFlow as salesFlowSelector,
  salesMethod as salesMethodSelector,
  sellflowRoot1,
  valuationStandardAddonsSelected
} from '../selectors/sellFlowSelectors';
import { featureFlagValueSelector } from '../selectors/featureFlagsSelector';
import { shouldHideValuationsForCorporateCars } from '~/App/views/SellCar/shared/helpers';
import { LeadObjectInformation } from './../interfaces/store/SellFlow';
import { SpecItem } from './../interfaces/TechnicalSpecification';
import {
  sellflowBusinessProposalFailure,
  sellflowBusinessProposalRequest,
  sellflowBusinessProposalSuccess,
  sellflowErrorFallbackSolution,
  sellflowOdometerFailure,
  sellflowOdometerLock,
  sellflowOdometerRequest,
  sellflowOdometerSuccess,
  sellflowReset,
  sellflowSalesAgreementFailure,
  sellflowSalesAgreementRequest,
  sellflowSalesAgreementSuccess,
  sellflowSetDefaultContactDetails,
  sellflowSetToken,
  sellflowTrimOptionsFailure,
  sellflowTrimOptionsLock,
  sellflowTrimOptionsRequest,
  sellflowTrimOptionsSuccess,
  sellflowValuationClose,
  sellflowVehicleInfoDefaults,
  sellflowVehicleInfoFailure,
  sellflowVehicleInfoRequest,
  sellflowVehicleInfoSuccess
} from './sellflowActions';
import { TranslateFunction } from '~/Locale';
import { logger } from '~/helpers/logger';
import { GA4ABTestExtraParams } from '~/helpers/client/ga4TrackEvent';
import { isEmpty } from '../utils';

const MAX_ATTEMPTS = 3;

type OmitFalsyWithDefaultsFunc = (
  args: (readonly unknown[])[]
) => Record<string, unknown>;

const omitFalsyWithDefaultsFromArrayPairs: OmitFalsyWithDefaultsFunc = pipe(
  reject(pipe(last, either(isNil, isEmpty))),
  fromPairs
);

const getSelectedAddons = (addons?: SpecItem[]): string[] =>
  addons ? addons.map(({ key }) => key) : [];

export const handleGetLeadObjectError = ({
  error,
  dispatch,
  t,
  action,
  storeAction
}: {
  error: AxiosError;
  dispatch: ReduxDispatch;
  t: TranslateFunction;
  action?: (errMsg: string) => void;
  storeAction?:
    | typeof sellflowVehicleInfoFailure
    | typeof sellflowOdometerFailure
    | typeof sellflowTrimOptionsFailure;
}) => {
  const shouldUseFallbackSolution = getShouldUseFallbackSolution(error);
  if (shouldUseFallbackSolution) {
    dispatch(sellflowErrorFallbackSolution());
  } else {
    const errorMsg = isAxiosError(error)
      ? getProposalErrorMessage(error, t)
      : error.message;
    storeAction && dispatch(storeAction(errorMsg));
    action && action(errorMsg);
  }
  requestErrorHandler(error);
  return shouldUseFallbackSolution;
};

export const vehicleInfoFetchVehicle =
  (trackSuccessCallback: () => void, t: TranslateFunction) =>
  (dispatch: ReduxDispatch, getState: () => ReduxStore) => {
    const {
      registrationPlate,
      prefilledRegistrationPlate,
      odometerReading,
      prefilledOdometerReadingInKilometers,
      packageSelected,
      addonsSelected,
      valuationStandardAddonsSelected
    } = sellflowRoot1(getState());
    dispatch(sellflowVehicleInfoRequest());

    // Use defaults in specific order and omit anything that's falsy
    const params = omitFalsyWithDefaultsFromArrayPairs([
      ['odometerReading', prefilledOdometerReadingInKilometers],
      ['odometerReading', odometerReading],
      ['registrationPlate', prefilledRegistrationPlate],
      ['registrationPlate', registrationPlate],
      ['package', packageSelected],
      ['addons', getSelectedAddons(addonsSelected)],
      [
        'valuation_standard_addons',
        map(prop('key'), valuationStandardAddonsSelected)
      ],
      ['odometer_unit', 'km'],
      ['country', 'SE']
    ]);

    getLeadObject({
      params
    })
      .then((resp: LeadObjectInformation) => {
        if (resp.salesFlow) {
          dispatch(sellflowVehicleInfoSuccess(resp));
          dispatch(sellflowVehicleInfoDefaults(resp));
          scroller.scrollTo('vehicle-info', {
            duration: 750,
            delay: 100,
            smooth: true,
            isDynamic: true
          });
          trackSuccessCallback();
        } else {
          dispatch(sellflowErrorFallbackSolution());
        }
      })
      .catch((error: AxiosError) =>
        handleGetLeadObjectError({
          error,
          dispatch,
          t,
          storeAction: sellflowVehicleInfoFailure
        })
      );
  };

export const odometerFetchVehicle =
  (trackSuccessCallback: () => void, t: TranslateFunction) =>
  (dispatch: ReduxDispatch, getState: () => ReduxStore) => {
    const {
      registrationPlate,
      prefilledRegistrationPlate,
      odometerReading,
      prefilledOdometerReadingInKilometers,
      packageSelected,
      addonsSelected,
      valuationStandardAddonsSelected,
      isTrimOptionsEdited
    } = sellflowRoot1(getState());
    dispatch(sellflowOdometerRequest());

    // Use defaults in specific order and omit anything that's falsy
    const params = omitFalsyWithDefaultsFromArrayPairs([
      ['odometerReading', prefilledOdometerReadingInKilometers],
      ['odometerReading', odometerReading],
      ['registrationPlate', prefilledRegistrationPlate],
      ['registrationPlate', registrationPlate],
      ['package', packageSelected],
      ['addons', getSelectedAddons(addonsSelected)],
      [
        'valuation_standard_addons',
        map(prop('key'), valuationStandardAddonsSelected)
      ],
      ['odometer_unit', 'km'],
      ['country', 'SE']
    ]);
    getLeadObject({
      params
    })
      .then((resp: LeadObjectInformation) => {
        if (resp?.salesFlow) {
          dispatch(sellflowOdometerLock(false));
          dispatch(sellflowOdometerSuccess(resp));
          const scrollTarget = isTrimOptionsEdited
            ? 'trim-options'
            : 'odometer';
          scroller.scrollTo(scrollTarget, {
            duration: 750,
            delay: 100,
            smooth: true,
            isDynamic: true
          });

          trackSuccessCallback();
        } else {
          dispatch(sellflowErrorFallbackSolution());
        }
      })
      .catch((error: AxiosError) =>
        handleGetLeadObjectError({
          error,
          dispatch,
          t,
          storeAction: sellflowOdometerFailure
        })
      );
  };

export const trimOptionsFetchVehicle =
  (
    trackSuccessCallback: () => void,
    t: TranslateFunction,
    valuationGatekeeperEmail?: string
  ) =>
  (dispatch: ReduxDispatch, getState: () => ReduxStore) => {
    const {
      registrationPlate,
      prefilledRegistrationPlate,
      odometerReading,
      prefilledOdometerReadingInKilometers,
      packageSelected,
      addonsSelected,
      valuationStandardAddonsSelected
    } = sellflowRoot1(getState());
    dispatch(sellflowTrimOptionsRequest());

    // Use defaults in specific order and omit anything that's falsy
    const params = omitFalsyWithDefaultsFromArrayPairs([
      ['odometerReading', prefilledOdometerReadingInKilometers],
      ['odometerReading', odometerReading],
      ['registrationPlate', prefilledRegistrationPlate],
      ['registrationPlate', registrationPlate],
      ['package', packageSelected],
      ['addons', getSelectedAddons(addonsSelected)],
      [
        'valuation_standard_addons',
        map(prop('key'), valuationStandardAddonsSelected)
      ],
      ['odometer_unit', 'km'],
      ['country', 'SE'],
      ['email', valuationGatekeeperEmail]
    ]);

    getLeadObject({
      params
    })
      .then((resp: LeadObjectInformation) => {
        if (resp?.salesFlow) {
          dispatch(sellflowTrimOptionsLock(true));
          dispatch(sellflowTrimOptionsSuccess(resp));
          resp.requiresManualValuation && !resp.isTradeInAvailable
            ? dispatch(sellflowValuationClose(true))
            : dispatch(sellflowValuationClose(false));
          scroller.scrollTo('trim-options', {
            duration: 750,
            delay: 100,
            smooth: true,
            isDynamic: true
          });

          trackSuccessCallback();
        } else {
          dispatch(sellflowErrorFallbackSolution());
        }
      })
      .catch((error: AxiosError) =>
        handleGetLeadObjectError({
          error,
          dispatch,
          t,
          storeAction: sellflowTrimOptionsFailure
        })
      );
  };

export interface CreateProposalInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isTradeIn: boolean;
  whenToContact: string;
  navigateCallback: CallableFunction;
  whenToSell: string;
  campaignCode: string | null;
  abTestExperiments?: GA4ABTestExtraParams[];
}
export const createProposal =
  (
    t: TranslateFunction,
    {
      firstName,
      lastName,
      email,
      phone,
      isTradeIn,
      whenToSell,
      whenToContact,
      campaignCode,
      navigateCallback,
      abTestExperiments
    }: CreateProposalInput,
    trackSuccessCallback: (clientMemberId?: string) => void
  ) =>
  (dispatch: ReduxDispatch, getState: () => ReduxStore) => {
    const state = getState();
    const salesFlow = salesFlowSelector(state);
    const salesMethod = salesMethodSelector(state);
    const hideValuationsForCorporateCars = featureFlagValueSelector(
      'hide-valuations-for-corporate-cars-in-e-flow'
    )(state);

    const shouldHideValuations = shouldHideValuationsForCorporateCars(
      Boolean(hideValuationsForCorporateCars),
      salesFlow
    );

    const retrackClick = getRetrackClick();
    const affiliateTracking: AffiliateTrackingInput | undefined = retrackClick
      ? { partner: 'refunder', trackingData: { clickid: retrackClick } }
      : undefined;

    const businessProposal: BusinessProposalInput = {
      isTradeIn,
      includedAddons: {
        package: packageSelected(state),
        addons: addonsSelected(state),
        valuationStandardAddons: valuationStandardAddonsSelected(state)
      },
      odometerReading: isNaN(odometerReading(state))
        ? 0
        : Number(odometerReading(state)),
      clientMemberFirstName: firstName,
      clientMemberLastName: lastName,
      clientMemberPhone: phone,
      clientMemberEmail: email,
      clientMemberTermsAccepted: true,
      whenToSell: whenToSell,
      ...(whenToContact === '__AB_TEST_HIDDEN__' ? {} : { whenToContact }),
      registrationPlate: registrationPlate(state),
      initiationType: LEAD_INITIATION_TYPES.PUBLIC_WEBSITE,
      campaignCode: campaignCode,
      abTestExperiments,
      affiliateTracking
    };

    dispatch(sellflowBusinessProposalRequest());
    createBusinessProposal({ businessProposal })
      .then(res => {
        const { requiresManualValuation = false, clientMemberId } =
          res.businessProposal;

        const effectiveRequiresManualValuation =
          requiresManualValuation || shouldHideValuations;

        const route = translateRoute(
          t,
          getDoneRouteForBusinessProposal({
            salesFlow: salesFlow ?? 'CONSUMER_CARS',
            isManualValuationRequired: effectiveRequiresManualValuation,
            isTradeIn,
            salesMethod,
            baseObjectId: res.businessProposal.baseObjectId,
            token: res.token
          })
        );

        dispatch(sellflowBusinessProposalSuccess(res));

        trackSuccessCallback(clientMemberId);

        navigateCallback(route);
      })
      .catch((error: AxiosError | Error) => {
        const errorMsg = isAxiosError(error)
          ? getProposalErrorMessage(error, t)
          : error.message;
        dispatch(sellflowBusinessProposalFailure(errorMsg));
        requestErrorHandler(error);
      });
  };

export interface CreateNoticeOfInterestInput extends CreateProposalInput {
  ticketRole: string;
  odometerReading: number;
  registrationPlate: string;
  extraTires?: boolean;
  otherInformation?: string;
}
export const createNoticeOfInterest =
  (
    t: TranslateFunction,
    {
      firstName,
      lastName,
      email,
      phone,
      whenToSell,
      ticketRole,
      campaignCode,
      odometerReading,
      registrationPlate,
      extraTires,
      otherInformation,
      navigateCallback
    }: CreateNoticeOfInterestInput,
    trackSuccessCallback: () => void
  ) =>
  (dispatch: ReduxDispatch) => {
    const inquiry = {
      email,
      phone,
      fullName: `${firstName} ${lastName}`,
      whenToSell,
      ticketRole,
      campaignCode,
      odometerReading,
      extraTires,
      otherInformation,
      registrationPlate
    };

    dispatch(sellflowBusinessProposalRequest());

    createInquiry({ noticeOfInterest: inquiry })
      .then(() => {
        dispatch(sellflowReset());
        trackSuccessCallback();
        dispatch(
          sellflowSetDefaultContactDetails(initialDefaultContactDetails)
        );
        navigateCallback(
          translateRoute(
            t,
            '/ROUTES.SELL/ROUTES.SELLFLOW_CONFIRM_OR_CONTINUE_COMPANY'
          )
        );
      })
      .catch((error: AxiosError | Error) => {
        const status = isAxiosError(error) ? error.response?.status : 0;
        const defaultMessage = 'Something went wrong, please try again later.';
        const errorMessage =
          status === 400
            ? pipe(
                pathOr([defaultMessage], ['response', 'data', 'message']),
                values,
                flatten,
                join(' ')
              )(error)
            : defaultMessage;

        dispatch(sellflowBusinessProposalFailure(errorMessage));
        requestErrorHandler(error);
      });
  };

export interface ReservationPriceObj {
  id: string;
  reservationPrice: { reservationPrice: number; token: string };
}

export const getBankIdSignin =
  (
    t: TranslateFunction,
    {
      id,
      token,
      history,
      pushRoute
    }: {
      id: string;
      token: string;
      history?: ReturnType<typeof useHistory>;
      pushRoute?: string;
    }
  ) =>
  (dispatch: ReduxDispatch) => {
    dispatch(sellflowSalesAgreementRequest());
    getBankIdAutoStartToken({ id, token })
      .then(response => {
        dispatch(sellflowSalesAgreementSuccess(response));
        history && pushRoute && history.push(translateRoute(t, pushRoute));
      })
      .catch((error: AxiosError | Error) => {
        dispatch(
          sellflowSalesAgreementFailure(
            error.message || 'could not request bankid token'
          )
        );
        requestErrorHandler(error);
      });
  };

type ProposalProps = {
  token: string;
  baseObjectId: string;
  attempt?: number;
  t: TranslateFunction;
};

export const getBusinessProposalWithSalesAgreement =
  ({ token, baseObjectId, attempt = 1, t }: ProposalProps) =>
  async (dispatch: ReduxDispatch) => {
    if (token && baseObjectId) {
      dispatch(sellflowBusinessProposalRequest());

      try {
        const businessProposal = await getProposal({ id: baseObjectId, token });
        dispatch(
          sellflowBusinessProposalSuccess({
            businessProposal
          })
        );

        if (businessProposal.salesAgreement) {
          dispatch(
            sellflowSalesAgreementSuccess(businessProposal.salesAgreement)
          );
        }
        dispatch(sellflowSetToken(token));
      } catch (error: unknown) {
        if (attempt > MAX_ATTEMPTS) {
          const errorMsg = isAxiosError(error)
            ? getProposalErrorMessage(error, t)
            : (error as Error).message;
          requestErrorHandler(error);
          dispatch(sellflowBusinessProposalFailure(errorMsg));
        } else {
          await retryBusinessProposal(
            {
              token,
              baseObjectId,
              attempt,
              t
            },
            dispatch
          );
        }
      }
    }
  };

// This is used to be able to clear the timeout
let timeoutID: number;

// TODO: make this a helper function, to be able to use in other places
async function retryBusinessProposal(
  { token, baseObjectId, attempt, t }: ProposalProps,
  dispatch: ReduxDispatch
) {
  return new Promise(resolve => {
    if (timeoutID) clearTimeout(timeoutID);
    timeoutID = window.setTimeout(() => {
      getBusinessProposalWithSalesAgreement({
        token,
        baseObjectId,
        attempt: attempt && attempt + 1,
        t
      })(dispatch)
        .then(() => {
          resolve(true);
        })
        .catch(err => {
          logger.error(err.message, { error: err });
        });

      clearTimeout(timeoutID);
    }, 1000);
  });
}
